/**
 * NamespaceContext
 *
 * Manages current namespace, list, permissions, and modal state with caching
 * and user-change detection. Exposes helpers to load, switch, create, and
 * reset namespace-related state.
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { getNamespaces, clearNamespaceCache } from '../../api/namespaces/namespacesApi';
import { useAuth } from '../auth';
import AuthLoadingScreen from '../../components/shared/AuthLoadingScreen';

const NamespaceContext = createContext();

// Key for storing the current namespace in localStorage
const CURRENT_NAMESPACE_KEY = 'currentNamespace';
const LAST_USER_ID_KEY = 'lastUserId';

// Function to clear all user-specific caches
const clearAllUserCaches = () => {
  // Clear namespace cache
  clearNamespaceCache();
  
  // Clear API cache from window
  if (window.__API_CACHE) {
    window.__API_CACHE = {};
    console.log('NamespaceContext: Cleared general API cache');
  }
  
  // Clear any other user-specific cache items
  const cacheKeys = Object.keys(localStorage).filter(key => 
    key.includes('cache') || 
    key.includes('timestamp') || 
    key.includes('user_') ||
    key.includes('api_')
  );
  
  cacheKeys.forEach(key => {
    localStorage.removeItem(key);
  });
  
  console.log('NamespaceContext: Cleared all user-specific caches');
};

export function NamespaceProvider({ children }) {
  const { user, isProfileLoaded } = useAuth();
  const [currentNamespace, setCurrentNamespace] = useState(() => {
    // Only get the namespace from localStorage if we have a user
    // We'll validate this against the current user later
    const savedNamespace = localStorage.getItem(CURRENT_NAMESPACE_KEY);
    return savedNamespace || 'default';
  });
  const [isNamespaceModalOpen, setIsNamespaceModalOpen] = useState(false);
  const [namespaces, setNamespaces] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userPermission, setUserPermission] = useState(null);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);

  // Check if user has changed and reset namespace if needed
  useEffect(() => {
    if (user?.uid) {
      const lastUserId = localStorage.getItem(LAST_USER_ID_KEY);
      
      if (lastUserId && lastUserId !== user.uid) {
        // User has changed, reset namespace to default and clear all caches
        console.log('NamespaceContext: User changed, resetting namespace from previous user');
        
        // Clear all user-specific caches to ensure fresh data for new user
        clearAllUserCaches();
        
        setCurrentNamespace('default');
        localStorage.removeItem(CURRENT_NAMESPACE_KEY);
        setNamespaces([]);
        setUserPermission(null);
        setError(null);
        setHasInitialLoad(false);
      }
      
      // Update the stored user ID
      localStorage.setItem(LAST_USER_ID_KEY, user.uid);
    } else if (!user && localStorage.getItem(LAST_USER_ID_KEY)) {
      // User logged out, clear the stored user ID and all caches
      localStorage.removeItem(LAST_USER_ID_KEY);
      clearAllUserCaches();
    }
  }, [user?.uid]);

  // Simple loading function - caching logic is in the getNamespaces function
  const loadNamespaces = async (forceRefresh = false) => {
    if (!user) return;
    
    // Only set loading to true if this is not the initial load or if we're forcing a refresh
    if (!hasInitialLoad || forceRefresh) {
      setIsLoading(true);
    }
    setError(null);
    
    try {
      const fetchedNamespaces = await getNamespaces({ forceRefresh });
      
      if (fetchedNamespaces && Array.isArray(fetchedNamespaces)) {
        setNamespaces(fetchedNamespaces);
        
        // If current namespace is not in the list, set to default or first available
        if (fetchedNamespaces.length > 0) {
          const namespaceNames = fetchedNamespaces.map(ns => ns.name);
          if (!namespaceNames.includes(currentNamespace)) {
            const newNamespace = namespaceNames[0];
            console.log(`NamespaceContext: Current namespace "${currentNamespace}" not found, switching to "${newNamespace}"`);
            setCurrentNamespace(newNamespace);
          }
        }
      } else {
        console.error('Invalid namespaces data format:', fetchedNamespaces);
        setNamespaces([]);
        setError('Unable to load your namespaces. Please try again or contact support if the issue persists.');
      }
    } catch (err) {
      console.error('Error loading namespaces:', err);
      setError('Failed to load namespaces. Please try again later.');
    } finally {
      setIsLoading(false);
      setHasInitialLoad(true);
    }
  };

  // Fetch namespaces when user is loaded and authenticated
  useEffect(() => {
    if (user && isProfileLoaded && !hasInitialLoad) {
      // Force refresh if this is after a user change to ensure fresh data
      const lastUserId = localStorage.getItem(LAST_USER_ID_KEY);
      const forceRefresh = lastUserId !== user.uid;
      
      if (forceRefresh) {
        console.log('NamespaceContext: Forcing refresh due to user change detection');
      }
      
      loadNamespaces(forceRefresh);
    }
  }, [user, isProfileLoaded, hasInitialLoad]);

  // Save current namespace to localStorage when it changes (but only if we have a user)
  useEffect(() => {
    if (user?.uid) {
      localStorage.setItem(CURRENT_NAMESPACE_KEY, currentNamespace);
      
      // Update userPermission when currentNamespace changes
      if (namespaces.length > 0) {
        const currentNamespaceObj = namespaces.find(ns => ns.name === currentNamespace);
        if (currentNamespaceObj) {
          setUserPermission(currentNamespaceObj.currentUserPermission || null);
        } else {
          setUserPermission(null);
        }
      }
    }
  }, [currentNamespace, namespaces, user?.uid]);

  const openNamespaceModal = () => {
    setIsNamespaceModalOpen(true);
  };
  
  const closeNamespaceModal = () => setIsNamespaceModalOpen(false);
  
  const switchNamespace = (namespace) => {
    setCurrentNamespace(namespace);
    closeNamespaceModal();
  };

  const createNamespace = async (namespace) => {
    await loadNamespaces(true); // Force refresh
    
    if (namespace) {
      switchNamespace(namespace);
    }
  };

  // For backward compatibility, provide fetchUserNamespaces as alias to loadNamespaces
  const fetchUserNamespaces = (force) => loadNamespaces(force);

  // Add a reset function to clear namespace state when logging out
  const resetNamespaceContext = () => {
    setNamespaces([]);
    setCurrentNamespace('default');
    setUserPermission(null);
    setError(null);
    setHasInitialLoad(false);
    localStorage.removeItem(CURRENT_NAMESPACE_KEY);
    localStorage.removeItem(LAST_USER_ID_KEY);
    clearAllUserCaches(); // Clear all user-specific caches
    console.log('NamespaceContext: Reset namespace state and cleared all caches');
  };

  const value = {
    currentNamespace,
    namespaces,
    isNamespaceModalOpen,
    isLoading,
    error,
    userPermission,
    openNamespaceModal,
    closeNamespaceModal,
    switchNamespace,
    createNamespace,
    fetchUserNamespaces, // Export the function with the old name for backward compatibility
    resetNamespaceContext // Add reset function to the context value
  };

  return (
    <NamespaceContext.Provider value={value}>
      {/* SIMPLIFIED: Always render children, let AppLayout handle loading states */}
      {children}
    </NamespaceContext.Provider>
  );
}

// Hook to use the namespace context
const useNamespace = () => {
  const context = useContext(NamespaceContext);
  if (!context) {
    throw new Error('useNamespace must be used within a NamespaceProvider');
  }
  return context;
};

// Export hook as part of the default export to fix Fast Refresh
export { useNamespace };

